import type { HistoryChange, HistoryObjectType } from '@fos/shared/contracts/audit';
import { historyActionText } from '@fos/shared/labels';
import { History } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { formatRelativePast } from '../../lib/format';
import { trpc } from '../../lib/trpc';
import { Avatar } from '../../ui/Avatar';
import { Button } from '../../ui/Button';
import { EmptyState, ErrorState, ListSkeleton } from '../../ui/States';

/**
 * Historia zmian rekordu (audit_log): kto, kiedy, co zmienił. Prezentacja (`HistoryList`) przyjmuje wpisy
 * z `audit.listForObject` albo z karty zadania (`TaskDetail.history`, ten sam kształt bez `summary`).
 * Wartości przychodzą z serwera gotowe do wyświetlenia (etykiety PL, nazwy powiązań); pole ukryte
 * (długi tekst, kwota bez uprawnienia) pokazuje „zmieniono”.
 */
export interface HistoryEntryLike {
  id: string;
  at: Date | string;
  action: string;
  summary?: string;
  subject?: string | null;
  actor: { userId: string; name: string; initials: string; avatarColor: string | null } | null;
  actorType: string;
  via: string;
  changes: Array<Pick<HistoryChange, 'field' | 'label' | 'from' | 'to'> & { hidden?: boolean }>;
  detail: string | null;
}

function actorName(e: HistoryEntryLike): string {
  if (e.actor) return e.actor.name;
  if (e.actorType === 'ai') return 'AI';
  if (e.actorType === 'worker') return 'Automat';
  return 'System';
}

export function HistoryList({
  entries,
  emptyText = 'Brak zmian.',
}: {
  entries: HistoryEntryLike[];
  emptyText?: string;
}) {
  if (entries.length === 0) return <p className="text-body-s text-fg-muted">{emptyText}</p>;
  const now = new Date();
  return (
    <ol className="flex flex-col" aria-label="Historia zmian">
      {entries.map((e) => (
        <li key={e.id} className="flex gap-3 border-b border-line py-3 last:border-b-0">
          {e.actor ? (
            <Avatar initials={e.actor.initials} color={e.actor.avatarColor} title={e.actor.name} />
          ) : (
            <Avatar ai={e.actorType === 'ai'} initials="S" title={actorName(e)} />
          )}
          <div className="min-w-0 flex-1">
            <p className="text-body-s text-fg">
              <span className="font-medium">{actorName(e)}</span>
              <span className="text-fg-secondary"> · {e.summary ?? historyActionText(e.action, e.changes)}</span>
              <span className="text-fg-muted"> · {formatRelativePast(e.at, now)}</span>
              {e.via === 'ai' || e.via === 'mcp' ? (
                <span className="text-fg-muted"> · {e.via === 'ai' ? 'przez AI' : 'przez MCP'}</span>
              ) : null}
            </p>
            {e.subject && <p className="mt-0.5 text-caption text-fg-secondary">{e.subject}</p>}
            {e.detail && e.changes.length === 0 && <p className="mt-0.5 text-caption text-fg-muted">„{e.detail}”</p>}
            {e.changes.length > 0 && (
              <ul className="mt-1 flex flex-col gap-0.5 text-caption text-fg-secondary">
                {e.changes.map((c) => (
                  <li key={c.field}>
                    <span className="text-fg-muted">{c.label}: </span>
                    {c.hidden ? (
                      'zmieniono'
                    ) : (
                      <>
                        {c.from ?? '—'} → {c.to ?? '—'}
                      </>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
}

/**
 * Historia rekordu z serwera (`audit.listForObject`): stany ładowania, błędu i pusty, „Pokaż starsze”.
 * `refreshKey`: zmiana (np. nowy obiekt rekordu po zapisie) odświeża historię.
 */
export function ObjectHistory({
  objectType,
  objectId,
  includeRelated = true,
  pageSize = 30,
  refreshKey,
  className,
}: {
  objectType: HistoryObjectType;
  objectId: string;
  includeRelated?: boolean;
  pageSize?: number;
  refreshKey?: unknown;
  className?: string;
}) {
  const [limit, setLimit] = useState(pageSize);
  const query = trpc.audit.listForObject.useQuery(
    { objectType, objectId, includeRelated, limit },
    {
      staleTime: 0,
      // „Pokaż starsze” (większy limit) zostawia dotychczasowe wpisy; inny rekord — nigdy cudza historia
      placeholderData: (prev, prevQuery) => {
        const key = prevQuery?.queryKey as unknown[] | undefined;
        const prevInput = (key?.[1] as { input?: { objectId?: string; objectType?: string } } | undefined)?.input;
        return prevInput?.objectId === objectId && prevInput?.objectType === objectType ? prev : undefined;
      },
    },
  );
  const first = useRef(true);
  // biome-ignore lint/correctness/useExhaustiveDependencies: odświeżenie tylko po zmianie rekordu
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    void query.refetch();
  }, [refreshKey]);

  return (
    <section aria-label="Historia zmian" className={className}>
      {query.isPending ? (
        <ListSkeleton rows={3} />
      ) : query.isError ? (
        <ErrorState compact onRetry={() => void query.refetch()} retrying={query.isFetching} />
      ) : query.data.items.length === 0 ? (
        <EmptyState compact icon={History} title="Brak zapisanych zmian." />
      ) : (
        <>
          <HistoryList entries={query.data.items} />
          {query.data.truncated && (
            <div className="pt-2">
              <Button variant="ghost" size="s" loading={query.isFetching} onClick={() => setLimit((l) => l + pageSize)}>
                Pokaż starsze
              </Button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
